<template>
  <div class="login-form-with-flow flexing flex-col" :class="`login-form-with-flow--${context}`">
    <transition
      :name="transitionName"
      mode="out-in"
      @before-leave="onBeforeLeave"
      @enter="onEnter"
      @after-enter="onAfterEnter"
    >
      <template v-if="currentStep === LoginFlowStep.EnterEmail">
        <login-form-enter-email
          v-model:email="form.email"
          :is-loading="isLoading"
          :errors="errors"
          :context="context"
          @continue="onContinueClick"
        />
      </template>

      <template
        v-else-if="
          [
            LoginFlowStep.EnterPasswordAndSignIn,
            LoginFlowStep.EnterPasswordAndSignUp,
            LoginFlowStep.EnterPasswordAndConnect,
          ].includes(currentStep)
        "
      >
        <login-form-enter-pwd
          v-model:password="form.password"
          :email="form.email"
          :is-loading="isLoading"
          :errors="errors"
          :current-step="currentStep"
          :is-sign-up="currentStep === LoginFlowStep.EnterPasswordAndSignUp"
          :is-sign-in="currentStep === LoginFlowStep.EnterPasswordAndSignIn"
          :is-connect="currentStep === LoginFlowStep.EnterPasswordAndConnect"
          @enter-email="setStep(LoginFlowStep.EnterEmail)"
          @forgotten-pwd="setStep(LoginFlowStep.ForgottenPassword)"
          @submit="
            currentStep === LoginFlowStep.EnterPasswordAndConnect
              ? onConnectClick()
              : onPlayNowClick()
          "
          @back="setStep(LoginFlowStep.EnterEmail)"
        />
      </template>

      <template v-else-if="currentStep === LoginFlowStep.EmailVerification">
        <login-form-email-verification
          :email="form.email"
          :is-loading="isLoading"
          :context="context"
          :is-email-change="isEmailChange"
          :is-connection="context === LoginFormContext.CONNECTION"
          @change-email="setStep(LoginFlowStep.EmailChange)"
        />
      </template>

      <template v-else-if="currentStep === LoginFlowStep.ForgottenPassword">
        <login-form-forgotten-pwd
          v-model:email="form.email"
          :is-loading="isLoading"
          :errors="errors.email"
          :show-back-btn="showBackBtnForgottenPwd"
          @submit="onForgottenPasswordSubmit"
          @back="setStep(LoginFlowStep.EmailChange)"
          @login="onEnterEmailWithEmailPrefilledClick"
        />
      </template>

      <template v-else-if="currentStep === LoginFlowStep.ForgottenPasswordMessage">
        <login-form-forgotten-pwd-message
          :is-loading="isLoading"
          :is-error="isError"
          @back="setStep(LoginFlowStep.ForgottenPassword)"
          @login="onEnterEmailWithEmailPrefilledClick"
        />
      </template>

      <template v-else-if="currentStep === LoginFlowStep.EmailAlreadySentErrorMessage">
        <login-form-email-sent-message
          :is-loading="isLoading"
          @back="setStep(LoginFlowStep.ForgottenPassword)"
        />
      </template>

      <template v-else-if="currentStep === LoginFlowStep.EnterNewPassword">
        <login-form-new-pwd
          v-model:password="form.password"
          v-model:password-confirmation="form.passwordConfirmation"
          :is-loading="isLoading"
          :errors="errors"
          @submit="onNewPasswordSubmit"
          @login="onEnterEmailWithEmailPrefilledClick"
        />
      </template>

      <template v-else-if="currentStep === LoginFlowStep.EnterNewPasswordMessage">
        <login-form-new-pwd-message
          :is-loading="isLoading"
          :is-error="isError"
          @back="setStep(LoginFlowStep.EnterNewPassword)"
          @login="onEnterEmailWithEmailPrefilledClick"
        />
      </template>

      <template v-else-if="currentStep === LoginFlowStep.EmailChange">
        <login-form-email-change
          v-model:email="form.email"
          v-model:password="form.password"
          :is-loading="isLoading"
          :errors="errors"
          @back="setStep(LoginFlowStep.ForgottenPassword)"
          @submit="onEmailChangeSubmit"
        />
      </template>
    </transition>
  </div>
</template>

<script lang="ts">
import {
  LOGIN_VIEW_ROUTE_NAME,
  RESET_PASSWORD_ROUTE_NAME,
  checkEmailRegisteredEndpoint as CHECK_EMAIL_REGISTERED_ENPOINT,
  loginEndpoint as LOGIN_ENDPOINT,
  registerEndpoint as REGISTER_ENDPOINT,
  dispatchGameLogin as DISPATCH_GAME_LOGIN,
  passwordForgotEndpoint as PASSWORD_FORGOT_ENDPOINT,
  passwordResetEndpoint as PASSWORD_RESET_ENDPOINT,
  addLoginMethodEndpoint as ADD_LOGIN_METHOD_ENDPOINT,
  userProfileEndpoint as USER_PROFILE_ENDPOINT,
  GameId,
} from '@/globalVariables'
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH } from '@/data/login/constants'
import {
  gameToken as GAME_TOKEN,
  detectLanguage,
  gameToken,
  saveTokenToLocalStorage,
  sendToFlutter,
  validateEmail,
} from '@/helpers'
import type CheckEmailRegisteredRequest from '@/interfaces/responses/login-flow/CheckEmailRegisteredRequest'
import type CheckEmailRegisteredResponse from '@/interfaces/responses/login-flow/CheckEmailRegisteredResponse'
import type {
  LoginApiResponse,
  LoginApiRequest,
  RegistrationApiResponse,
  RegistrationApiRequest,
  ForgotPasswordApiResponse,
  ForgotPasswordApiRequest,
  ResetPasswordApiRequest,
  ResetPasswordApiResponse,
} from '@/interfaces/responses/login/LoginApiResponse'
import { useCountriesStore } from '@/store/pinia/countriesStore'
import { useUserStore } from '@/store/pinia/userStore'
import { useCoreStore } from '@/store/pinia/coreStore'
import { useAccountStore } from '@/store/pinia/accountStore'
import { useResponseTaskStore } from '@/store/pinia/responseTaskStore'
import { defineComponent, type PropType } from 'vue'
import { mapActions, mapState } from 'pinia'
import { ConnectedAccountStatus, ConnectedAccountType, LoginFormContext } from '@/enums/LoginForm'
import { LoginFlowStep } from '@/enums/LoginFlowStep'
import type { Nullable, TrueFalseInterface } from '@/interfaces/utils'
import type { UserProfile as UserProfileApiResponse } from '@/interfaces/User'
import type { Form, FormErrors } from '@/interfaces/login/Login'
import LoginFormEmailChange from './LoginFormEmailChange.vue'
import LoginFormNewPwdMessage from './LoginFormNewPwdMessage.vue'
import LoginFormNewPwd from './LoginFormNewPwd.vue'
import LoginFormEmailSentMessage from './LoginFormEmailSentMessage.vue'
import LoginFormForgottenPwdMessage from './LoginFormForgottenPwdMessage.vue'
import LoginFormForgottenPwd from './LoginFormForgottenPwd.vue'
import LoginFormEmailVerification from './LoginFormEmailVerification.vue'
import LoginFormEnterPwd from './LoginFormEnterPwd.vue'
import LoginFormEnterEmail from './LoginFormEnterEmail.vue'

export interface ComponentData {
  LOGIN_VIEW_ROUTE_NAME: typeof LOGIN_VIEW_ROUTE_NAME
  PASSWORD_MIN_LENGTH: typeof PASSWORD_MIN_LENGTH
  PASSWORD_MAX_LENGTH: typeof PASSWORD_MAX_LENGTH
  LoginFormContext: typeof LoginFormContext
  ConnectedAccountStatus: typeof ConnectedAccountStatus
  GameId: typeof GameId
  LoginFlowStep: typeof LoginFlowStep
  currentStep: Nullable<LoginFlowStep>
  prevHeight: `${number}px` | 'auto'
  transitionName: 'slide-next' | 'slide-back' | ''
  form: Form
  errors: FormErrors
  games: Set<GameId>
  isLoading: boolean
  isError: boolean
}

export default defineComponent({
  components: {
    LoginFormEmailChange,
    LoginFormNewPwdMessage,
    LoginFormNewPwd,
    LoginFormEmailSentMessage,
    LoginFormForgottenPwdMessage,
    LoginFormForgottenPwd,
    LoginFormEmailVerification,
    LoginFormEnterPwd,
    LoginFormEnterEmail,
  },
  props: {
    context: {
      type: String as PropType<LoginFormContext>,
      default: '',
      validator: (value: LoginFormContext) => Object.values(LoginFormContext).includes(value),
    },
  },
  emits: ['reload-user-data'],
  data(): ComponentData {
    return {
      LOGIN_VIEW_ROUTE_NAME,
      PASSWORD_MIN_LENGTH,
      PASSWORD_MAX_LENGTH,
      GameId,
      LoginFormContext,
      ConnectedAccountStatus,
      LoginFlowStep,
      currentStep: null,
      prevHeight: 'auto',
      transitionName: 'slide-next',
      form: {
        email: (this.$route.query.email as string) || '',
        password: '',
        passwordConfirmation: '',
      },
      errors: {
        email: new Set<string>(),
        password: new Set<string>(),
        passwordConfirmation: new Set<string>(),
      },
      games: new Set<GameId>(),
      isLoading: false,
      isError: false,
    }
  },
  computed: {
    ...mapState(useCoreStore, [
      'hasBadCredentials',
      'emailAlreadySent',
      'emailRegisteredElsewhere',
      'isInvalidEmail',
      'emailNotRegistered',
    ]),
    ...mapState(useUserStore, {
      user: 'getUserData',
    }),
    ...mapState(useAccountStore, ['connectedAccounts']),
    ...mapState(useResponseTaskStore, {
      validationErrors: 'getValidationErrors',
    }),
    isRegistration(): boolean {
      return this.games.size === 0
    },
    isPasswordReset(): boolean {
      return (
        this.context === LoginFormContext.PASSWORD_RESET ||
        this.$route.name === RESET_PASSWORD_ROUTE_NAME
      )
    },
    isEmailChange(): boolean {
      return this.context === LoginFormContext.EMAIL_CHANGE
    },
    isEmailVerified(): boolean {
      return (
        this.context === LoginFormContext.CONNECTION &&
        this.connectedAccounts?.email === ConnectedAccountStatus.Connected
      )
    },
    isEmailNotVerified(): boolean {
      return (
        this.context === LoginFormContext.CONNECTION &&
        this.connectedAccounts?.email === ConnectedAccountStatus.VerificationPending
      )
    },
    hasErrors(): boolean {
      return (
        this.errors.email.size > 0 ||
        this.errors.password.size > 0 ||
        this.errors.passwordConfirmation.size > 0
      )
    },
    showBackBtnForgottenPwd(): boolean {
      return [LoginFormContext.CONNECTION, LoginFormContext.EMAIL_CHANGE].includes(this.context)
    },
  },
  watch: {
    isInvalidEmail(value: boolean): void {
      if (!value) return
      this.errors.email.add(this.$t('homepage.errorEmailInvalidFormat'))
      this.removeAxiosErrors()
    },
    hasBadCredentials(value: boolean): void {
      if (!value) return
      this.errors.password.add(this.$t('error.badCredentials'))
      this.removeAxiosErrors()
    },
    emailRegisteredElsewhere(value: boolean): void {
      if (!value) return
      this.errors.email.add(this.$t('homepage.errorEmailRegisteredElsewhere'))
      this.removeAxiosErrors()
    },
    emailAlreadySent(value: boolean): void {
      if (!value) return
      this.setStep(LoginFlowStep.EmailAlreadySentErrorMessage)
      this.removeAxiosErrors()
    },
    emailNotRegistered(value: boolean): void {
      if (!value) return
      this.errors.email.add(this.$t('homepage.forgotPasswordError'))
      this.removeAxiosErrors()
    },
    validationErrors(value: Nullable<string[]>): void {
      if (!value || !value.length) return
      if (!value.includes('emailAlreadyRegistered')) return
      this.errors.email.add(this.$t('settings.emailAlreadyRegistered'))
      this.removeValidationErrors()
    },
    currentStep(to: LoginFlowStep, from: LoginFlowStep | null): void {
      if (from === LoginFlowStep.EnterEmail) this.transitionName = 'slide-next'
      if (to === LoginFlowStep.EnterEmail) this.transitionName = 'slide-back'

      if (from === LoginFlowStep.EnterPasswordAndSignIn && to === LoginFlowStep.ForgottenPassword)
        this.transitionName = 'slide-next'
      if (from === LoginFlowStep.ForgottenPassword && to === LoginFlowStep.EnterPasswordAndSignIn)
        this.transitionName = 'slide-back'

      if (from === LoginFlowStep.EnterNewPassword && to === LoginFlowStep.EnterNewPassword)
        this.transitionName = 'slide-next'
      if (from === LoginFlowStep.EnterNewPassword && to === LoginFlowStep.EnterPasswordAndSignIn)
        this.transitionName = 'slide-back'

      if (
        from === LoginFlowStep.EnterNewPasswordMessage &&
        to === LoginFlowStep.EnterPasswordAndSignIn
      )
        this.transitionName = 'slide-back'

      if (from === LoginFlowStep.EmailChange && to === LoginFlowStep.EmailVerification)
        this.transitionName =
          this.context === LoginFormContext.CONNECTION ? 'slide-back' : 'slide-next'
      if (from === LoginFlowStep.EmailVerification && to === LoginFlowStep.EmailChange)
        this.transitionName =
          this.context === LoginFormContext.CONNECTION ? 'slide-next' : 'slide-back'

      if (from === LoginFlowStep.EmailChange && to === LoginFlowStep.ForgottenPassword)
        this.transitionName = 'slide-next'
      if (from === LoginFlowStep.ForgottenPassword && to === LoginFlowStep.EmailChange)
        this.transitionName = 'slide-back'

      if (from === null) {
        this.transitionName = ''
      }

      if (this.currentStep === LoginFlowStep.EmailChange) {
        this.form.email = ''
      }

      this.form.password = ''
      this.form.passwordConfirmation = ''

      this.errors.email.clear()
      this.errors.password.clear()
    },
    'form.email'(): void {
      this.errors.email.clear()
      this.removeAxiosErrors()

      if (this.validationErrors?.length) {
        this.removeValidationErrors()
      }
    },
    'form.password'(): void {
      this.errors.password.clear()
      this.removeAxiosErrors()

      if (this.validationErrors?.length) {
        this.removeValidationErrors()
      }
    },
    'form.passwordConfirmation'(): void {
      this.errors.passwordConfirmation.clear()
      this.removeAxiosErrors()

      if (this.validationErrors?.length) {
        this.removeValidationErrors()
      }
    },
    '$route.query.email': {
      handler(): void {
        if (
          !this.$route.query.email ||
          this.$route.name !== LOGIN_VIEW_ROUTE_NAME ||
          this.currentStep !== LoginFlowStep.EnterEmail
        )
          return

        this.onContinueClick()
      },
      immediate: true,
    },
    'connectedAccounts.email': {
      handler(): void {
        if (this.context !== LoginFormContext.CONNECTION) return
        this.initCurrentStep()
      },
      deep: true,
    },
  },
  async created(): Promise<void> {
    await this.loadLanguages(false)

    if (this.context === LoginFormContext.CONNECTION) {
      await this.loadConnectedAccounts()
    }

    this.initCurrentStep()

    sendToFlutter('{"event":"logout"}')
  },
  methods: {
    ...mapActions(useCountriesStore, ['loadLanguages']),
    ...mapActions(useCoreStore, ['removeAxiosErrors', 'setLoadingScreen']),
    ...mapActions(useAccountStore, ['loadConnectedAccounts']),
    ...mapActions(useResponseTaskStore, ['removeValidationErrors']),
    initCurrentStep(): void {
      if (this.isEmailVerified) {
        this.setStep(LoginFlowStep.EnterEmail)
        this.form.email = this.user.email
        return
      }

      if (this.isEmailNotVerified) {
        this.setStep(LoginFlowStep.EmailVerification)
        this.form.email = this.user.email
        return
      }

      if (this.isPasswordReset) {
        this.setStep(LoginFlowStep.EnterNewPassword)
        return
      }

      if (this.isEmailChange) {
        this.setStep(LoginFlowStep.EmailChange)
        return
      }

      this.setStep(LoginFlowStep.EnterEmail)
    },
    setStep(step: Nullable<LoginFlowStep>): void {
      this.currentStep = step
    },
    validate(): boolean {
      // validate e-mail field
      if (
        [
          LoginFlowStep.EnterEmail,
          LoginFlowStep.EnterPasswordAndSignIn,
          LoginFlowStep.EnterPasswordAndSignUp,
          LoginFlowStep.ForgottenPassword,
          LoginFlowStep.EnterPasswordAndConnect,
          LoginFlowStep.EmailChange,
        ].includes(this.currentStep)
      ) {
        this.errors.email.clear()

        if (!this.form.email) this.errors.email.add(this.$t('homepage.errorEmailInvalidFormat'))
        else if (!validateEmail(this.form.email))
          this.errors.email.add(this.$t('homepage.errorEmailInvalidFormat'))
      }

      // validate password field
      if (
        [
          LoginFlowStep.EnterPasswordAndSignIn,
          LoginFlowStep.EnterPasswordAndSignUp,
          LoginFlowStep.EnterNewPassword,
          LoginFlowStep.EnterPasswordAndConnect,
          LoginFlowStep.EmailChange,
        ].includes(this.currentStep)
      ) {
        this.errors.password.clear()

        if (!this.form.password)
          this.errors.password.add(this.$t('homepage.errorPasswordInvalidFormat'))
        else if (this.form.password.length < this.PASSWORD_MIN_LENGTH)
          this.errors.password.add(this.$t('homepage.errorShortPassword'))
        else if (this.form.password.length > this.PASSWORD_MAX_LENGTH)
          this.errors.password.add(this.$t('homepage.errorLongPassword'))
      }

      // validate password confirmation field
      if (this.currentStep === LoginFlowStep.EnterNewPassword) {
        this.errors.passwordConfirmation.clear()

        if (!this.form.passwordConfirmation)
          this.errors.passwordConfirmation.add(this.$t('homepage.errorPasswordInvalidFormat'))
        else if (this.form.passwordConfirmation.length < this.PASSWORD_MIN_LENGTH)
          this.errors.passwordConfirmation.add(this.$t('homepage.errorShortPassword'))
        else if (this.form.passwordConfirmation.length > this.PASSWORD_MAX_LENGTH)
          this.errors.passwordConfirmation.add(this.$t('homepage.errorLongPassword'))

        if (this.form.passwordConfirmation !== this.form.password)
          this.errors.passwordConfirmation.add(this.$t('settings.newPasswordsDoesNotMatch'))
      }

      return (
        this.errors.email.size === 0 &&
        this.errors.password.size === 0 &&
        this.errors.passwordConfirmation.size === 0
      )
    },
    async onContinueClick(): Promise<void> {
      if (!this.validate()) return

      try {
        this.isLoading = true

        const response = await this.$axios.post<
          {},
          CheckEmailRegisteredResponse,
          CheckEmailRegisteredRequest
        >(CHECK_EMAIL_REGISTERED_ENPOINT, {
          email: this.form.email,
        })
        if (!response.games) return

        this.games = new Set(response.games)

        if (this.context === LoginFormContext.CONNECTION) {
          this.setStep(LoginFlowStep.EnterPasswordAndConnect)
        } else {
          const step = this.isRegistration
            ? LoginFlowStep.EnterPasswordAndSignUp
            : LoginFlowStep.EnterPasswordAndSignIn
          this.setStep(step)
        }
      } catch (error: unknown) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    onEnterEmailWithEmailPrefilledClick(): void {
      this.setStep(LoginFlowStep.EnterEmail)
      this.$router.push({
        name: LOGIN_VIEW_ROUTE_NAME,
        query: { email: this.form.email || undefined },
      })
    },
    async onPlayNowClick(): Promise<void> {
      return this.isRegistration ? this.onSignUpSubmit() : this.onSignInSubmit()
    },
    async onConnectClick(): Promise<void> {
      if (!this.validate()) return

      try {
        this.isLoading = true

        await this.$axios.post<
          {},
          TrueFalseInterface,
          {
            email: string
            password: string
            loginType: string
            game_token: string
            uuid: string
            platform: string
          }
        >(ADD_LOGIN_METHOD_ENDPOINT, {
          email: this.form.email,
          password: this.form.password,
          loginType: ConnectedAccountType.Email,
          game_token: gameToken,
          uuid: this.user.id,
          platform: 'web',
        })

        await this.loadConnectedAccounts(true)

        this.setStep(LoginFlowStep.EmailVerification)
      } catch (error: unknown) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    async onSignInSubmit(): Promise<void> {
      if (!this.validate()) return

      const lastIdGroup = JSON.parse(localStorage.getItem('last_id_group'))?.value ?? null

      try {
        this.isLoading = true

        const response = await this.$axios.post<{}, LoginApiResponse, LoginApiRequest>(
          LOGIN_ENDPOINT,
          {
            email: this.form.email,
            password: this.form.password,
            game_token: GAME_TOKEN,
            group_id: lastIdGroup,
          },
        )
        if (!response.access_token) return

        saveTokenToLocalStorage(response.access_token)
        this.setLoadingScreen(true)

        await this.$axios.post<{}, true>(DISPATCH_GAME_LOGIN)

        this.$router.push('/')
      } catch (error: unknown) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    async onSignUpSubmit(): Promise<void> {
      if (!this.validate()) return

      const lastIdGroup = JSON.parse(localStorage.getItem('last_id_group'))?.value ?? null

      try {
        this.isLoading = true

        const data = {
          email: this.form.email,
          password: this.form.password,
          game_token: GAME_TOKEN,
          language: detectLanguage(),
          last_id_group: lastIdGroup,
          id_group: null,
        }

        const idGroup = JSON.parse(localStorage.getItem('id_group'))
        if (idGroup) data.id_group = parseInt(idGroup.value)
        localStorage.clear()

        const response = await this.$axios.post<
          {},
          RegistrationApiResponse,
          RegistrationApiRequest
        >(REGISTER_ENDPOINT, data)
        if (!response.access_token) return

        saveTokenToLocalStorage(response.access_token)

        this.$router.push({
          name: this.$getWebView('ChooseDisciplineTutorial'),
        })
      } catch (error: unknown) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    async onForgottenPasswordSubmit(): Promise<void> {
      if (!this.validate()) return

      try {
        this.isError = false
        this.isLoading = true

        await this.$axios.post<{}, ForgotPasswordApiResponse, ForgotPasswordApiRequest>(
          PASSWORD_FORGOT_ENDPOINT,
          { email: this.form.email },
        )

        this.setStep(LoginFlowStep.ForgottenPasswordMessage)
      } catch (error: unknown) {
        console.error(error)
        this.isError = true
      } finally {
        this.isLoading = false
      }
    },
    async onNewPasswordSubmit(): Promise<void> {
      if (!this.validate()) return

      try {
        this.isError = false
        this.isLoading = true

        await this.$axios.post<{}, ResetPasswordApiResponse, ResetPasswordApiRequest>(
          PASSWORD_RESET_ENDPOINT,
          {
            token: this.$route.query.token as string,
            email: this.form.email,
            platform: 'web',
            password: this.form.password,
            password_confirmation: this.form.passwordConfirmation,
          },
        )

        this.setStep(LoginFlowStep.EnterNewPasswordMessage)
      } catch (error: unknown) {
        console.error(error)
        this.isError = true
      } finally {
        this.isLoading = false
      }
    },
    async onEmailChangeSubmit(): Promise<void> {
      if (!this.validate()) return

      try {
        this.isError = false
        this.isLoading = true

        const response = await this.$axios.post<
          {},
          UserProfileApiResponse,
          { email: string; password: string }
        >(USER_PROFILE_ENDPOINT, {
          email: this.form.email,
          password: this.form.password,
        })

        // Toto by bolo vhodne popridavat do vsetkych akcii, ale to si zase vyziada testing celeho komponentu.
        if (this.hasErrors || !response.user_data.email) return

        this.$emit('reload-user-data')

        // Nepotrebujeme await, moze sa dotiahnut na pozadi.
        this.loadConnectedAccounts(true)

        this.setStep(LoginFlowStep.EmailVerification)
      } catch (error: unknown) {
        console.error(error)
        this.isError = true
      } finally {
        this.isLoading = false
      }
    },
    onBeforeLeave(element: HTMLElement): void {
      this.prevHeight = getComputedStyle(element).height as `${number}px`
    },
    onEnter(element: HTMLElement): void {
      const { height } = getComputedStyle(element)
      element.style.height = this.prevHeight
      requestAnimationFrame((): void => {
        element.style.height = height
      })
    },
    onAfterEnter(element: HTMLElement): void {
      element.style.height = 'auto'
    },
  },
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/views/login/loginForm.scss';
</style>
